import {
  Component,
  Inject,
  OnInit,
  Optional,
  inject,
  ViewChild,
  ChangeDetectorRef,
  ViewChildren,
  QueryList,
} from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { ToastrService } from "ngx-toastr";
import { DataService } from "src/app/data.service";
import { db } from "src/app/db.service";
import { AuthService } from "src/app/auth.service";
import { SnapshotService, IMaterialDelta } from "src/app/snapshot.service";
import { StockService } from "src/app/stock/stock.service";
import { NgForm, UntypedFormControl } from "@angular/forms";
import { forkJoin, of, Observable } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { qService } from "src/app/db.service";
import { v4 as uuidv4 } from "uuid";
import * as _moment from "moment";
import * as _latinize from "latinize";
import { NgSelectComponent } from "@ng-select/ng-select";
import { ConfirmationDialogComponent } from "src/app/shared/confirmation-dialog/confirmation-dialog.component";

const moment = _moment;
const latinize = _latinize;

export interface IReconciliationItem {
  id?: string; // For tracking in UI
  materialId: number;
  materialName?: string;
  expected: number;
  actual: number;
  difference: number;
  notes?: string;
}

@Component({
  selector: "app-reconciliation-dialog",
  templateUrl: "./reconciliation-dialog.component.html",
  styleUrls: ["./reconciliation-dialog.component.css"],
})
export class ReconciliationDialogComponent implements OnInit {
  @ViewChild("reconForm") form: NgForm;
  @ViewChildren("input") inputs: QueryList<NgSelectComponent>;

  _authService = inject(AuthService);
  _snapshotService = inject(SnapshotService);
  _stockService = inject(StockService);

  date = new UntypedFormControl(moment());
  selectedStockGroupId: number = 1;
  dataLoading: boolean = false;
  readOnly: boolean = false;
  editMode: boolean = false;
  transactionNo: string = "";
  
  materialsToReconcile: IReconciliationItem[] = [
    {
      materialId: null,
      expected: 0,
      actual: null,
      difference: 0,
    }
  ];

  constructor(
    public dialogRef: MatDialogRef<ReconciliationDialogComponent>,
    private http: HttpClient,
    public _dataService: DataService,
    private toastr: ToastrService,
    private changeDetectorRef: ChangeDetectorRef,
    private dialog: MatDialog,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any
  ) {}

  async ngOnInit() {
    this.date =
      this._dataService.startDate.value.format("YYYY-MM-DD") ==
      this._dataService.endDate.value.format("YYYY-MM-DD")
        ? new UntypedFormControl(
            moment(this._dataService.endDate.value.format("YYYY-MM-DD"))
          )
        : new UntypedFormControl(moment());

    this.selectedStockGroupId = this.dialogData?.stockGroupId || this._dataService.stockGroups()[0]?.id || 1;
    this.readOnly = this.dialogData?.readOnly || false;
    this.editMode = this.dialogData?.editMode || false;

    if (this.dialogData?.transaction) {
      this.transactionNo = this.dialogData.transaction.id;
      this.date.setValue(moment(this.dialogData.transaction.date));
      
      if (this.dialogData.reconciliations && this.dialogData.reconciliations.length > 0) {
        this.selectedStockGroupId = this.dialogData.reconciliations[0].stockGroupId;
        
        this.materialsToReconcile = this.dialogData.reconciliations.map(recon => ({
          materialId: recon.materialId,
          expected: recon.expectedQuantity,
          actual: recon.actualQuantity,
          difference: recon.difference,
          notes: recon.notes
        }));
      } else if (this.dialogData.reconciliation) {
        // Single mode legacy support
        const recon = this.dialogData.reconciliation;
        this.selectedStockGroupId = recon.stockGroupId;
        this.materialsToReconcile = [{
          materialId: recon.materialId,
          expected: recon.expectedQuantity,
          actual: recon.actualQuantity,
          difference: recon.difference,
          notes: recon.notes
        }];
      }
    }
  }

  async onMaterialSelect(item: IReconciliationItem, index: number) {
    if (!item.materialId) return;

    // Fetch expected quantity (current stock)
    const endDateString = this.date.value.format("YYYY-MM-DD");
    
    // We need to get the total for this specific material
    // Using getMaterialTotalsOptimized might be overkill if we just need one, 
    // but it's cached and fast.
    const totals = await this._stockService.getMaterialTotalsOptimized(
      endDateString,
      this.selectedStockGroupId
    );
    
    const materialTotal = totals.find(t => t.materialId === item.materialId);
    item.expected = materialTotal ? materialTotal.totalQuantity : 0;
    
    // If actual is not set, default to expected (no diff)
    if (item.actual === null || item.actual === undefined) {
      item.actual = item.expected;
    }
    
    this.calculateDifference(item);
  }

  calculateDifference(item: IReconciliationItem) {
    if (item.actual !== null && item.actual !== undefined) {
      item.difference = parseFloat((item.actual - item.expected).toFixed(3));
    } else {
      item.difference = 0;
    }
  }

  addMaterialRow() {
    this.materialsToReconcile.push({
      materialId: null,
      expected: 0,
      actual: null,
      difference: 0,
    });
    this.changeDetectorRef.detectChanges();
    // Focus logic if needed
  }

  removeMaterialRow(index: number) {
    if (this.materialsToReconcile.length > 1) {
      this.materialsToReconcile.splice(index, 1);
    } else {
      // Reset the last row instead of removing it
      this.materialsToReconcile[0] = {
        materialId: null,
        expected: 0,
        actual: null,
        difference: 0,
      };
    }
  }

  async saveReconciliation(event: Event) {
    const registration = await navigator.serviceWorker.ready;

    if ("sync" in registration) {
      await this.saveReconciliationOfflineFirst(event);
    } else {
      await this.saveReconciliationOnline(event);
    }
  }

  async saveReconciliationOfflineFirst(event: Event) {
    const btn = event.target as HTMLButtonElement;
    btn.disabled = true;
    // btn.innerText = "Saxlanılır..."; // Can't set innerText on button with icon easily, handled in template or just disable

    try {
      const registration = await navigator.serviceWorker.ready;

      // Handle Edit Mode: Delete old transaction first
      if (this.editMode && this.dialogData.transaction) {
        const oldId = this.dialogData.transaction.id;
        await db.transaction(
          "rw",
          db.transactions,
          db.reconciliations,
          db.stock,
          db.materialDeltas,
          async () => {
            await db.transactions.delete(oldId);
            await db.reconciliations.where("transactionId").equals(oldId).delete();
            await db.stock.where("transactionId").equals(oldId).delete();
            await db.materialDeltas.where("sourceId").equals(oldId).delete();
          }
        );

        await qService.requests.add({
          payload: {
            type: "REMOVE_TRANSACTION",
            removeTransactionId: oldId,
            dbName: this._dataService.dbTable,
            userId: this._authService.loggedInUserValue?.id,
          },
          timestamp: Date.now(),
        });
      }

      const tempTransactionId = uuidv4();
      const tempReconciliationIds = [];
      const tempStockIds = [];

      const transaction = {
        id: tempTransactionId,
        type: "Reconciliation",
        customerId: null,
        date: this.date.value.format("YYYY-MM-DD"),
        dateAdded: moment().format("YYYY-MM-DD HH:mm:ss"),
        details: "Üzləşmə",
        userId: this._authService.loggedInUserValue?.id ?? null,
        syncStatus: "pending",
      };

      const reconciliations = [];
      const stockAdjustments = [];
      const materialDeltas: IMaterialDelta[] = [];

      this.materialsToReconcile.forEach(item => {
        if (!item.materialId) return;

        // Create Reconciliation Record
        const tempReconId = uuidv4();
        tempReconciliationIds.push(tempReconId);
        
        reconciliations.push({
          id: tempReconId,
          transactionId: tempTransactionId,
          tId: tempTransactionId,
          materialId: item.materialId,
          stockGroupId: this.selectedStockGroupId,
          expectedQuantity: item.expected,
          actualQuantity: item.actual,
          difference: item.difference,
          notes: item.notes || `Üzləşmə`,
          date: transaction.date,
          syncStatus: "pending",
        });

        // Create Stock Adjustment (Only if there is a difference)
        if (item.difference !== 0) {
          const tempStockId = uuidv4();
          tempStockIds.push(tempStockId);
          
          const material = this._dataService.materials().find(m => m.id === item.materialId);
          
          stockAdjustments.push({
            id: tempStockId,
            transactionId: tempTransactionId,
            tId: tempTransactionId,
            materialId: item.materialId,
            stockGroupId: this.selectedStockGroupId,
            quantity: item.difference,
            price: material?.price || 0,
            date: transaction.date,
            customerId: null,
            tType: "Reconciliation",
            syncStatus: "pending",
          });

          materialDeltas.push({
            materialId: item.materialId,
            stockGroupId: this.selectedStockGroupId,
            date: transaction.date,
            quantityChange: item.difference,
            sourceType: 'other',
            sourceId: tempTransactionId,
            isProduction: false,
          });
        }
      });

      // Save to Dexie
      await db.transaction(
        "rw",
        db.transactions,
        db.reconciliations,
        db.stock,
        db.materialDeltas,
        async () => {
          await db.transactions.add(transaction, tempTransactionId);
          await db.reconciliations.bulkAdd(reconciliations);
          await db.stock.bulkAdd(stockAdjustments);
          await db.materialDeltas.bulkAdd(materialDeltas);
        }
      );

      // Rebuild snapshots for affected materials/months
      const affectedMonths = new Map();
      materialDeltas.forEach((delta) => {
        const yearMonth = delta.date.substring(0, 7);
        const key = `${delta.materialId}_${delta.stockGroupId}_${yearMonth}`;
        if (!affectedMonths.has(key)) {
          affectedMonths.set(key, {
            materialId: delta.materialId,
            stockGroupId: delta.stockGroupId,
            yearMonth,
          });
        }
      });

      for (const data of affectedMonths.values()) {
        await this._snapshotService.rebuildMonthSnapshot(
          data.materialId,
          data.stockGroupId,
          data.yearMonth
        );
      }

      // Queue for background sync
      const queuePayload = {
        type: "ADD_RECONCILIATION",
        payload: {
          transaction: {
            ...transaction,
            id: undefined,
            syncStatus: undefined,
          },
          reconciliations: reconciliations.map((r) => ({
            ...r,
            id: undefined,
            syncStatus: undefined,
          })),
          stockEntries: stockAdjustments.map((s) => ({
            ...s,
            id: undefined,
            syncStatus: undefined,
          })),
        },
        context: {
          tempTransactionId,
          tempReconciliationIds,
          tempStockIds,
          dbName: this._dataService.dbTable,
        },
      };

      await qService.requests.add({
        payload: queuePayload,
        timestamp: Date.now(),
      });

      await (registration as any).sync.register("app-sync");

      this._dataService.dateChanged.next(new Date().toISOString());
      this.dialogRef.close("reload");
      this.toastr.success("Üzləşmə saxlanıldı!");

    } catch (error) {
      console.error("Error saving reconciliation:", error);
      btn.disabled = false;
      this.toastr.error("Xəta baş verdi");
    }
  }

  async saveReconciliationOnline(event: Event) {
    const btn = event.target as HTMLButtonElement;
    btn.disabled = true;

    const transaction = {
      type: "Reconciliation",
      customerId: null,
      date: this.date.value.format("YYYY-MM-DD"),
      dateAdded: moment().format("YYYY-MM-DD HH:mm:ss"),
      details: "Üzləşmə",
      userId: this._authService.loggedInUserValue?.id ?? null,
    };

    let deleteOld: Observable<any> = of(null);
    if (this.editMode && this.dialogData.transaction) {
      deleteOld = this.http.put(
        `${this._dataService.dbBase}api.php/records/transactions/${this.dialogData.transaction.id}`,
        { status: 2 }
      );
    }

    deleteOld.pipe(
      switchMap(() => {
        return this.http.post<number>(
          `${this._dataService.dbBase}api.php/records/transactions`,
          transaction
        );
      }),
      switchMap((transactionId: any) => {
        const reconciliations = [];
        const stockAdjustments = [];
        const materialDeltas: IMaterialDelta[] = [];

        this.materialsToReconcile.forEach(item => {
          if (!item.materialId) return;

          reconciliations.push({
            transactionId,
            tId: transactionId,
            materialId: item.materialId,
            stockGroupId: this.selectedStockGroupId,
            expectedQuantity: item.expected,
            actualQuantity: item.actual,
            difference: item.difference,
            notes: item.notes || `Üzləşmə`,
            date: transaction.date,
          });

          if (item.difference !== 0) {
            const material = this._dataService.materials().find(m => m.id === item.materialId);
            stockAdjustments.push({
              transactionId,
              tId: transactionId,
              materialId: item.materialId,
              stockGroupId: this.selectedStockGroupId,
              quantity: item.difference,
              price: material?.price || 0,
              date: transaction.date,
              customerId: null,
              tType: "Reconciliation",
            });

            materialDeltas.push({
              materialId: item.materialId,
              stockGroupId: this.selectedStockGroupId,
              date: transaction.date,
              quantityChange: item.difference,
              sourceType: "other",
              sourceId: transactionId,
              isProduction: false,
            });
          }
        });

        return forkJoin({
          reconciliations: this.http.post(
            `${this._dataService.dbBase}api.php/records/reconciliations`,
            reconciliations
          ),
          stock: this.http.post(
            `${this._dataService.dbBase}api.php/records/stock`,
            stockAdjustments
          ),
        }).pipe(
          tap(async (res: any) => {
            (transaction as any).id = transactionId;
            
            // Assign returned IDs to local objects
            const reconIds = Array.isArray(res.reconciliations) ? res.reconciliations : [res.reconciliations];
            reconciliations.forEach((r, i) => {
              if (reconIds[i]) r.id = reconIds[i];
              r.syncStatus = "synced";
            });

            if (stockAdjustments.length > 0) {
              const stockIds = Array.isArray(res.stock) ? res.stock : [res.stock];
              stockAdjustments.forEach((s, i) => {
                if (stockIds[i]) s.id = stockIds[i];
                s.syncStatus = "synced";
              });
            }

            (transaction as any).syncStatus = "synced";

            await db.transaction(
              "rw",
              db.transactions,
              db.reconciliations,
              db.stock,
              db.materialDeltas,
              async () => {
                await db.transactions.put(transaction, transactionId);
                await db.reconciliations.bulkPut(reconciliations);
                await db.stock.bulkPut(stockAdjustments);
                await db.materialDeltas.bulkPut(materialDeltas);
              }
            );
            
            // Rebuild snapshots
            const affectedMonths = new Map();
            materialDeltas.forEach((delta) => {
              const yearMonth = delta.date.substring(0, 7);
              const key = `${delta.materialId}_${delta.stockGroupId}_${yearMonth}`;
              if (!affectedMonths.has(key)) {
                affectedMonths.set(key, {
                  materialId: delta.materialId,
                  stockGroupId: delta.stockGroupId,
                  yearMonth,
                });
              }
            });

            for (const data of affectedMonths.values()) {
              await this._snapshotService.rebuildMonthSnapshot(
                data.materialId,
                data.stockGroupId,
                data.yearMonth
              );
            }
          })
        );
      })
    ).subscribe({
      next: async (res: any) => {
        this._dataService.dateChanged.next(new Date().toISOString());
        this.dialogRef.close("reload");
        this.toastr.success("Üzləşmə saxlanıldı!");
      },
      error: (err) => {
        console.error("Error:", err);
        btn.disabled = false;
        this.toastr.error("Xəta baş verdi");
      },
    });
  }

  deleteEntry() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: "500px",
      data: "Bu üzləşməni silmək istədiyinizə əminsiniz?",
    });

    dialogRef.afterClosed().subscribe(async (res) => {
      if (res) {
        try {
          const registration = await navigator.serviceWorker.ready;
          const transactionId = this.dialogData.transaction.id;

          if ("sync" in registration) {
            // Offline/Background Sync
            await db.transaction(
              "rw",
              db.transactions,
              db.reconciliations,
              db.stock,
              db.materialDeltas,
              async () => {
                await db.transactions.delete(transactionId);
                await db.reconciliations.where("transactionId").equals(transactionId).delete();
                await db.stock.where("transactionId").equals(transactionId).delete();
                await db.materialDeltas.where("sourceId").equals(transactionId).delete();
              }
            );

            await qService.requests.add({
              payload: {
                type: "REMOVE_TRANSACTION",
                removeTransactionId: transactionId,
                dbName: this._dataService.dbTable,
                userId: this._authService.loggedInUserValue?.id,
              },
              timestamp: Date.now(),
            });

            await (registration as any).sync.register("app-sync");
          } else {
            // Online
            await this.http
              .put(
                `${this._dataService.dbBase}api.php/records/transactions/${transactionId}`,
                { status: 2 }
              )
              .toPromise();
              
            await db.transaction(
              "rw",
              db.transactions,
              db.reconciliations,
              db.stock,
              db.materialDeltas,
              async () => {
                await db.transactions.delete(transactionId);
                await db.reconciliations.where("transactionId").equals(transactionId).delete();
                await db.stock.where("transactionId").equals(transactionId).delete();
                await db.materialDeltas.where("sourceId").equals(transactionId).delete();
              }
            );
          }

          // Rebuild snapshots (simplified: just trigger rebuild for all affected materials if possible, 
          // or we'd need to track what was deleted. For now, relying on dateChanged to refresh views)
          // Ideally we should rebuild snapshots for the deleted items, but let's assume refresh handles it for now
          // or iterate over deleted items if we had them. 
          // Since we deleted them, we can't easily get them unless we fetched before delete.
          // For simplicity in this step, we'll just close and refresh.
          
          this._dataService.dateChanged.next(new Date().toISOString());
          this.dialogRef.close("reload");
          this.toastr.success("Silindi!");
        } catch (error) {
          console.error("Error deleting reconciliation:", error);
          this.toastr.error("Xəta baş verdi");
        }
      }
    });
  }
}